import { css } from '@emotion/react';
import {
  PhoneHoursExpansion,
  CallObject,
  Node,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PhoneHours } from '@frontend/phones#svg';
import { theme } from '@frontend/theme';
import { Accordion, Heading, Text, TextLink } from '@frontend/design-system';
import { DayHoursList } from '../../phone-hours/day-hours-list';

export type PhoneHoursCallObject = Extract<CallObject, { phoneHoursExpansion: PhoneHoursExpansion }>;
export type OfficeHoursObject = Extract<Node, { callObject: PhoneHoursCallObject }>;

export function isPhoneHoursCallObject(callObject: CallObject): callObject is PhoneHoursCallObject {
  return 'phoneHoursExpansion' in callObject;
}

type PhoneHoursPanelProps = {
  officeHourNodes: Node[];
  onEditIconClick: ({ nodeId }: { nodeId: string }) => void;
};

export const PhoneHoursPanel = ({ officeHourNodes, onEditIconClick }: PhoneHoursPanelProps) => {
  const { t } = useTranslation('phone');
  const phoneHourCallObjects: PhoneHoursCallObject[] = officeHourNodes
    .map((item) => item.callObject)
    .filter(isPhoneHoursCallObject);

  return (
    <>
      {phoneHourCallObjects.length === 0 && (
        <div
          css={css`
            width: 100%;
            display: flex;
            justify-content: center;
          `}
        >
          <div
            css={css`
              width: 336px;
            `}
          >
            <PhoneHours />
            <Text textAlign='center' color='subdued'>
              {t('No Phone Hours.')}
            </Text>
            <Text textAlign='center' color='subdued'>
              {t('Add a Phone Hours step in your Call Route to route calls differently by time of day.')}
            </Text>
          </div>
        </div>
      )}

      {phoneHourCallObjects.length === 1 && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(2)};
          `}
        >
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Heading level={3}>{phoneHourCallObjects[0].primitiveName}</Heading>
            <TextLink
              onClick={() => onEditIconClick?.({ nodeId: officeHourNodes[0].id })}
              css={{ display: 'flex', gap: theme.spacing(0.5), width: 'auto', alignItems: 'center' }}
              weight='bold'
            >
              <Icon name='edit' /> {t('Edit')}
            </TextLink>
          </div>
          <DayHoursList useLargerFonts phoneHours={phoneHourCallObjects[0].phoneHoursExpansion.phoneHours} />
        </div>
      )}

      {officeHourNodes.length > 1 &&
        officeHourNodes
          .filter((item) => isPhoneHoursCallObject(item.callObject))
          .map((item, index) => {
            const callObject = item.callObject as PhoneHoursCallObject;
            const { phoneHoursExpansion } = callObject;
            return (
              <Accordion
                key={callObject.primitiveName + index}
                css={css`
                  > div {
                    box-shadow: none;
                    border: 1px solid ${theme.colors.neutral20};
                  }

                  > div h3 button section div svg {
                    fill: ${theme.colors.neutral70};
                  }

                  div[data-active='true'] h3 button {
                    background-color: ${theme.colors.neutral5};
                    border-bottom: 1px solid ${theme.colors.neutral10};
                  }
                `}
              >
                <Accordion.Item value={'phoneHours'}>
                  <Accordion.Header>
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: ${theme.spacing(2)};
                      `}
                    >
                      <Icon name='clock' />
                      <Heading level={3}>{callObject.primitiveName}</Heading>
                      <Icon
                        name='edit'
                        onClick={() => onEditIconClick?.({ nodeId: item.id })}
                        css={{ position: 'absolute', right: theme.spacing(7), color: theme.colors.neutral90 }}
                      />
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <DayHoursList useLargerFonts phoneHours={phoneHoursExpansion.phoneHours} />
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            );
          })}
    </>
  );
};
